import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useInView } from "react-intersection-observer";
import ProductCard from "./ProductCard/ProductCard";
import { useProductCategoryContext } from "../contexts/ProductCategoryContext";
import { Category, ProductCategoryFilter } from "../types/Category";
import { Icon } from "./liquid/LiquidPrimitives";
import { useCategories } from "./Header/hooks/useCategories";
import { useTemplateConfig } from "../templates/templateConfigContext";
import { trackMetric, trackMetricViewOnce } from "@ecommerce/analytics";

interface ProductListingPageProps {
    mode: "all-products" | "category" | "search";
    searchQuery?: string;
}

type ViewMode = "grid" | "list";

const DEFAULT_SORT = "newest";

const sortOptions = [
    { value: "newest", label: "Mas nuevo al mas viejo" },
    { value: "oldest", label: "Mas viejo al mas nuevo" },
    { value: "price-asc", label: "Precio: Menor a mayor" },
    { value: "price-desc", label: "Precio: Mayor a menor" },
    { value: "a-z", label: "A - Z" },
    { value: "z-a", label: "Z - A" },
];

function parseOptionalNumber(value: string): number | undefined {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : undefined;
}

function sortCategories(categories: Category[]) {
    return categories
        .filter((category) => category.sku && category.nombre)
        .slice()
        .sort((a, b) => {
            const orderDiff = (a.orden ?? 999999) - (b.orden ?? 999999);
            if (orderDiff !== 0) return orderDiff;
            return a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" });
        });
}

function buildFilter(
    priceFrom: string,
    priceTo: string,
    sortValue: string,
    pageSize = 20,
): ProductCategoryFilter {
    const precioDesde = parseOptionalNumber(priceFrom);
    const precioHasta = parseOptionalNumber(priceTo);
    const nextFilter: ProductCategoryFilter = {
        pagina: 1,
        tamanoPagina: pageSize,
        orden: sortValue || DEFAULT_SORT,
    };

    if (precioDesde !== undefined) nextFilter.precioDesde = precioDesde;
    if (precioHasta !== undefined) nextFilter.precioHasta = precioHasta;

    return nextFilter;
}

function getSortLabel(value?: string) {
    return sortOptions.find((option) => option.value === value)?.label || sortOptions[0].label;
}

interface FilterProps {
    categories: Category[];
    getSubcategories: (parentId: number) => Category[];
    categoriesLoading: boolean;
    activeSku?: string;
    priceFrom: string;
    priceTo: string;
    onPriceFromChange: (value: string) => void;
    onPriceToChange: (value: string) => void;
    onApplyPrice: () => void;
    onClearAll: () => void;
    onCategoryClick: (category: Category) => void;
    hasActiveFilters: boolean;
}

function CategoryFilterItem({
    category,
    getSubcategories,
    activeSku,
    level,
    expandedCategories,
    onToggleExpand,
    onCategoryClick,
}: {
    category: Category;
    getSubcategories: (parentId: number) => Category[];
    activeSku?: string;
    level: number;
    expandedCategories: number[];
    onToggleExpand: (categoryId: number) => void;
    onCategoryClick: (category: Category) => void;
}) {
    const subcategories = sortCategories(getSubcategories(category.id));
    const hasSubcategories = subcategories.length > 0;
    const isExpanded = expandedCategories.includes(category.id);
    const active = category.sku === activeSku;

    return (
        <>
            <button
                type="button"
                onClick={() =>
                    hasSubcategories ? onToggleExpand(category.id) : onCategoryClick(category)
                }
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "8px 10px",
                    paddingLeft: 10 + level * 16,
                    borderRadius: 10,
                    border: "none",
                    background: active ? "rgba(0,98,156,0.08)" : "transparent",
                    cursor: "pointer",
                    fontSize: 13,
                    textAlign: "left",
                }}
            >
                {!hasSubcategories && (
                    <span
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: 5,
                            flexShrink: 0,
                            border: active ? "1.5px solid #00629c" : "1.5px solid rgba(0,0,0,0.18)",
                            background: active ? "#00629c" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {active && <Icon.check color="white" />}
                    </span>
                )}
                <span
                    style={{
                        flex: 1,
                        color: active ? "#00629c" : "var(--ink-700)",
                        fontWeight: active || hasSubcategories ? 600 : 400,
                    }}
                >
                    {category.nombre}
                </span>
                {hasSubcategories && (
                    <span
                        style={{
                            display: "flex",
                            flexShrink: 0,
                            color: "var(--ink-500)",
                            transition: "transform .2s",
                            transform: isExpanded ? "rotate(180deg)" : "none",
                        }}
                    >
                        <Icon.chevron />
                    </span>
                )}
            </button>

            {hasSubcategories && isExpanded && (
                <>
                    <button
                        type="button"
                        onClick={() => onCategoryClick(category)}
                        style={{
                            display: "block",
                            width: "100%",
                            padding: "8px 10px",
                            paddingLeft: 10 + (level + 1) * 16,
                            borderRadius: 10,
                            border: "none",
                            background: active ? "rgba(0,98,156,0.08)" : "transparent",
                            cursor: "pointer",
                            fontSize: 12,
                            fontStyle: "italic",
                            textAlign: "left",
                            color: active ? "#00629c" : "var(--ink-500)",
                        }}
                    >
                        Ver todo en {category.nombre}
                    </button>

                    {subcategories.map((subcategory) => (
                        <CategoryFilterItem
                            key={subcategory.id}
                            category={subcategory}
                            getSubcategories={getSubcategories}
                            activeSku={activeSku}
                            level={level + 1}
                            expandedCategories={expandedCategories}
                            onToggleExpand={onToggleExpand}
                            onCategoryClick={onCategoryClick}
                        />
                    ))}
                </>
            )}
        </>
    );
}

function FilterRail(props: FilterProps) {
    const {
        categories,
        getSubcategories,
        categoriesLoading,
        activeSku,
        priceFrom,
        priceTo,
        onPriceFromChange,
        onPriceToChange,
        onApplyPrice,
        onClearAll,
        onCategoryClick,
        hasActiveFilters,
    } = props;

    const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

    const toggleExpand = (categoryId: number) => {
        setExpandedCategories((prev) =>
            prev.includes(categoryId)
                ? prev.filter((id) => id !== categoryId)
                : [...prev, categoryId],
        );
    };

    return (
        <aside
            className="lg-strong"
            style={{
                padding: "28px 26px",
                borderRadius: 28,
                position: "sticky",
                top: 88,
                alignSelf: "start",
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 22,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon.grid />
                    <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: "-0.01em" }}>
                        Filtros
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onClearAll}
                    disabled={!hasActiveFilters}
                    style={{
                        border: "none",
                        background: "transparent",
                        fontSize: 11,
                        color: hasActiveFilters ? "var(--ink-500)" : "var(--ink-300)",
                        cursor: hasActiveFilters ? "pointer" : "default",
                        textDecoration: hasActiveFilters ? "underline" : "none",
                    }}
                >
                    Limpiar
                </button>
            </div>

            <div style={{ marginBottom: 26 }}>
                <div className="eyebrow" style={{ marginBottom: 12 }}>
                    Categorias
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {categoriesLoading ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                color: "var(--ink-500)",
                                fontSize: 13,
                            }}
                        >
                            <CircularProgress size={14} sx={{ color: "var(--ink-500)" }} />
                            Cargando categorias...
                        </div>
                    ) : (
                        categories.map((category) => (
                            <CategoryFilterItem
                                key={category.id}
                                category={category}
                                getSubcategories={getSubcategories}
                                activeSku={activeSku}
                                level={0}
                                expandedCategories={expandedCategories}
                                onToggleExpand={toggleExpand}
                                onCategoryClick={onCategoryClick}
                            />
                        ))
                    )}
                </div>
            </div>

            <div>
                <div className="eyebrow" style={{ marginBottom: 12 }}>
                    Precio
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span className="mono" style={{ fontSize: 10, color: "var(--ink-500)" }}>
                            DESDE
                        </span>
                        <input
                            value={priceFrom}
                            onChange={(e) => onPriceFromChange(e.target.value)}
                            inputMode="decimal"
                            placeholder="$0"
                            style={{
                                height: 40,
                                borderRadius: 14,
                                border: "1px solid rgba(0,0,0,0.08)",
                                background: "rgba(255,255,255,0.55)",
                                padding: "0 12px",
                                outline: "none",
                                color: "var(--ink-900)",
                            }}
                        />
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span className="mono" style={{ fontSize: 10, color: "var(--ink-500)" }}>
                            HASTA
                        </span>
                        <input
                            value={priceTo}
                            onChange={(e) => onPriceToChange(e.target.value)}
                            inputMode="decimal"
                            placeholder="$999.999"
                            style={{
                                height: 40,
                                borderRadius: 14,
                                border: "1px solid rgba(0,0,0,0.08)",
                                background: "rgba(255,255,255,0.55)",
                                padding: "0 12px",
                                outline: "none",
                                color: "var(--ink-900)",
                            }}
                        />
                    </label>
                    <button
                        type="button"
                        className="pill"
                        onClick={onApplyPrice}
                        style={{ justifyContent: "center", marginTop: 4 }}
                    >
                        Aplicar precio
                    </button>
                </div>
            </div>
        </aside>
    );
}

function CategoryFilterPill({
    category,
    getSubcategories,
    activeSku,
    isOpen,
    onToggleOpen,
    onCategoryClick,
}: {
    category: Category;
    getSubcategories: (parentId: number) => Category[];
    activeSku?: string;
    isOpen: boolean;
    onToggleOpen: (categoryId: number) => void;
    onCategoryClick: (category: Category) => void;
}) {
    const subcategories = sortCategories(getSubcategories(category.id));
    const hasSubcategories = subcategories.length > 0;
    const active = category.sku === activeSku;

    return (
        <div style={{ position: "relative", flexShrink: 0 }}>
            <button
                type="button"
                onClick={() =>
                    hasSubcategories ? onToggleOpen(category.id) : onCategoryClick(category)
                }
                style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: active ? "#00629c" : "rgba(0,0,0,0.06)",
                    color: active ? "#ffffff" : "var(--ink-700)",
                    fontWeight: active ? 600 : 400,
                }}
            >
                {category.nombre}
                {hasSubcategories && (
                    <span
                        style={{
                            display: "flex",
                            transition: "transform .2s",
                            transform: isOpen ? "rotate(180deg)" : "none",
                        }}
                    >
                        <Icon.chevron />
                    </span>
                )}
            </button>

            {hasSubcategories && isOpen && (
                <div
                    className="lg-strong"
                    style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        zIndex: 200,
                        padding: 6,
                        borderRadius: 16,
                        minWidth: 200,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => onCategoryClick(category)}
                        style={{
                            padding: "8px 12px",
                            borderRadius: 10,
                            border: "none",
                            background: active ? "rgba(0,98,156,0.08)" : "transparent",
                            cursor: "pointer",
                            fontSize: 12,
                            fontStyle: "italic",
                            textAlign: "left",
                            color: active ? "#00629c" : "var(--ink-500)",
                        }}
                    >
                        Ver todo en {category.nombre}
                    </button>
                    {subcategories.map((subcategory) => {
                        const subActive = subcategory.sku === activeSku;
                        return (
                            <button
                                key={subcategory.id}
                                type="button"
                                onClick={() => onCategoryClick(subcategory)}
                                style={{
                                    padding: "8px 12px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: subActive ? "rgba(0,98,156,0.08)" : "transparent",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    textAlign: "left",
                                    color: subActive ? "#00629c" : "var(--ink-700)",
                                    fontWeight: subActive ? 600 : 400,
                                }}
                            >
                                {subcategory.nombre}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function FilterBarra(props: FilterProps) {
    const {
        categories,
        getSubcategories,
        categoriesLoading,
        activeSku,
        priceFrom,
        priceTo,
        onPriceFromChange,
        onPriceToChange,
        onApplyPrice,
        onClearAll,
        onCategoryClick,
        hasActiveFilters,
    } = props;
    const [bitPrecioOpen, setBitPrecioOpen] = useState(false);
    const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);

    const toggleCategoryOpen = (categoryId: number) => {
        setOpenCategoryId((prev) => (prev === categoryId ? null : categoryId));
    };

    return (
        <div
            className="lg-strong"
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                padding: "12px 16px",
                borderRadius: 16,
                marginBottom: 20,
                overflowX: "auto",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <Icon.grid />
                <span style={{ fontWeight: 600, fontSize: 13 }}>Filtrar:</span>
            </div>

            {categoriesLoading ? (
                <CircularProgress size={14} sx={{ color: "var(--ink-500)" }} />
            ) : (
                categories.map((category) => (
                    <CategoryFilterPill
                        key={category.id}
                        category={category}
                        getSubcategories={getSubcategories}
                        activeSku={activeSku}
                        isOpen={openCategoryId === category.id}
                        onToggleOpen={toggleCategoryOpen}
                        onCategoryClick={onCategoryClick}
                    />
                ))
            )}

            <div style={{ position: "relative", flexShrink: 0 }}>
                <button
                    type="button"
                    className="pill"
                    onClick={() => setBitPrecioOpen((o) => !o)}
                    style={{
                        padding: "6px 14px",
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    Precio <Icon.chevron />
                </button>
                {bitPrecioOpen && (
                    <div
                        className="lg-strong"
                        style={{
                            position: "absolute",
                            top: "calc(100% + 8px)",
                            left: 0,
                            zIndex: 200,
                            padding: 16,
                            borderRadius: 16,
                            minWidth: 220,
                            display: "grid",
                            gap: 10,
                        }}
                    >
                        <label style={{ display: "grid", gap: 4 }}>
                            <span
                                className="mono"
                                style={{ fontSize: 10, color: "var(--ink-500)" }}
                            >
                                DESDE
                            </span>
                            <input
                                value={priceFrom}
                                onChange={(e) => onPriceFromChange(e.target.value)}
                                inputMode="decimal"
                                placeholder="$0"
                                style={{
                                    height: 36,
                                    borderRadius: 10,
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    background: "rgba(255,255,255,0.55)",
                                    padding: "0 10px",
                                    outline: "none",
                                    color: "var(--ink-900)",
                                }}
                            />
                        </label>
                        <label style={{ display: "grid", gap: 4 }}>
                            <span
                                className="mono"
                                style={{ fontSize: 10, color: "var(--ink-500)" }}
                            >
                                HASTA
                            </span>
                            <input
                                value={priceTo}
                                onChange={(e) => onPriceToChange(e.target.value)}
                                inputMode="decimal"
                                placeholder="$999.999"
                                style={{
                                    height: 36,
                                    borderRadius: 10,
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    background: "rgba(255,255,255,0.55)",
                                    padding: "0 10px",
                                    outline: "none",
                                    color: "var(--ink-900)",
                                }}
                            />
                        </label>
                        <button
                            type="button"
                            className="pill"
                            onClick={() => {
                                onApplyPrice();
                                setBitPrecioOpen(false);
                            }}
                            style={{ justifyContent: "center" }}
                        >
                            Aplicar
                        </button>
                    </div>
                )}
            </div>

            {hasActiveFilters && (
                <button
                    type="button"
                    onClick={onClearAll}
                    style={{
                        border: "none",
                        background: "transparent",
                        fontSize: 12,
                        color: "var(--ink-500)",
                        cursor: "pointer",
                        textDecoration: "underline",
                        flexShrink: 0,
                    }}
                >
                    Limpiar
                </button>
            )}
        </div>
    );
}

function SortSelect({
    value,
    options,
    onChange,
}: {
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const selected = options.find((o) => o.value === value);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <button
                type="button"
                className="pill"
                onClick={() => setOpen((o) => !o)}
                style={{
                    padding: "10px 14px",
                    gap: 8,
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {selected?.label}
                <span
                    style={{
                        display: "flex",
                        transition: "transform .2s",
                        transform: open ? "rotate(180deg)" : "none",
                    }}
                >
                    <Icon.chevron />
                </span>
            </button>

            {open && (
                <div
                    className="lg-strong"
                    style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        right: 0,
                        minWidth: 220,
                        borderRadius: 18,
                        padding: 6,
                        zIndex: 200,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {options.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                                onChange(option.value);
                                setOpen(false);
                            }}
                            style={{
                                padding: "10px 14px",
                                borderRadius: 12,
                                border: "none",
                                background:
                                    option.value === value ? "rgba(0,98,156,0.08)" : "transparent",
                                color: option.value === value ? "#00629c" : "var(--ink-700)",
                                cursor: "pointer",
                                textAlign: "left",
                                fontSize: 13,
                                fontWeight: option.value === value ? 600 : 400,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            {option.label}
                            {option.value === value && <Icon.check />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function ListingHeader({
    title,
    count,
    mode,
    breadcrumbs,
    sortValue,
    viewMode,
    onSortChange,
    onViewModeChange,
}: {
    title: string;
    count: number;
    mode: "all-products" | "category" | "search";
    breadcrumbs: { label: string; href?: string }[];
    sortValue: string;
    viewMode: ViewMode;
    onSortChange: (value: string) => void;
    onViewModeChange: (value: ViewMode) => void;
}) {
    const isMobile = useMediaQuery("(max-width:768px)");
    const displayBreadcrumbs =
        mode === "search"
            ? [{ label: "Inicio", href: "/" }, { label: "Busqueda" }]
            : breadcrumbs.length > 0
              ? breadcrumbs
              : [{ label: "Inicio", href: "/" }, { label: title }];

    return (
        <div
            style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "flex-end",
                justifyContent: "space-between",
                marginBottom: 20,
                padding: "0 8px",
                gap: isMobile ? 16 : 24,
            }}
        >
            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "var(--ink-500)",
                        marginBottom: 12,
                        flexWrap: "wrap",
                    }}
                >
                    {displayBreadcrumbs.map((item, index) => (
                        <React.Fragment key={`${item.label}-${index}`}>
                            <span>{item.label}</span>
                            {index < displayBreadcrumbs.length - 1 && (
                                <span style={{ color: "var(--ink-300)" }}>›</span>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                <h1
                    style={{
                        fontSize: isMobile ? 28 : 44,
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        margin: 0,
                        lineHeight: 1,
                    }}
                >
                    {title}
                </h1>
                <div style={{ marginTop: 12, fontSize: 14, color: "var(--ink-500)" }}>
                    <span className="mono" style={{ fontWeight: 600, color: "#00629c" }}>
                        {count}
                    </span>{" "}
                    productos en stock
                </div>
            </div>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "var(--ink-500)" }}>Ordenar por</span>
                    <SortSelect value={sortValue} options={sortOptions} onChange={onSortChange} />
                </div>

                <div
                    style={{
                        display: "flex",
                        padding: 4,
                        borderRadius: 999,
                        background: "rgba(10,10,10,0.06)",
                    }}
                >
                    <button
                        type="button"
                        onClick={() => onViewModeChange("grid")}
                        style={{
                            padding: "8px 12px",
                            borderRadius: 999,
                            border: "none",
                            background: viewMode === "grid" ? "#00629c" : "transparent",
                            color: viewMode === "grid" ? "#ffffff" : "var(--ink-500)",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 12,
                            fontWeight: viewMode === "grid" ? 500 : 400,
                        }}
                    >
                        <Icon.grid /> Grid
                    </button>
                    <button
                        type="button"
                        onClick={() => onViewModeChange("list")}
                        style={{
                            padding: "8px 12px",
                            borderRadius: 999,
                            border: "none",
                            background: viewMode === "list" ? "#00629c" : "transparent",
                            color: viewMode === "list" ? "#ffffff" : "var(--ink-500)",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 12,
                            fontWeight: viewMode === "list" ? 500 : 400,
                        }}
                    >
                        <Icon.list /> Lista
                    </button>
                </div>
            </div>
        </div>
    );
}

function ActiveFilters({
    chips,
}: {
    chips: { key: string; label: string; onClear?: () => void }[];
}) {
    if (chips.length === 0) return null;

    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 24,
                padding: "0 8px",
            }}
        >
            {chips.map((chip) => (
                <button
                    key={chip.key}
                    type="button"
                    className="pill"
                    onClick={chip.onClear}
                    style={{
                        padding: "8px 14px",
                        fontSize: 12,
                        cursor: chip.onClear ? "pointer" : "default",
                    }}
                >
                    {chip.label}
                    {chip.onClear && <Icon.close />}
                </button>
            ))}
        </div>
    );
}

const ProductListingPage: React.FC<ProductListingPageProps> = ({ mode, searchQuery }) => {
    const { sku } = useParams<{ sku: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery("(max-width:768px)");
    const { config } = useTemplateConfig();
    const posicionFiltros = config.layout.vchPosicionFiltros ?? "izquierda";
    const [ref, inView] = useInView({ threshold: 0, rootMargin: "100px" });
    const { rootCategories, getSubcategories, loading: categoriesLoading } = useCategories();
    const [sortValue, setSortValue] = useState(DEFAULT_SORT);
    const [priceFrom, setPriceFrom] = useState("");
    const [priceTo, setPriceTo] = useState("");
    const [viewMode, setViewMode] = useState<ViewMode>("grid");

    const {
        category,
        products,
        loading,
        loadingMore,
        error,
        breadcrumbs,
        pagination,
        filter,
        loadCategoryData,
        loadAllProductsData,
        loadSearchResults,
        loadMoreProducts,
        saveListingCache,
    } = useProductCategoryContext();

    const sortedCategories = useMemo(() => sortCategories(rootCategories), [rootCategories]);

    const applyFilter = async (
        nextPriceFrom = priceFrom,
        nextPriceTo = priceTo,
        nextSort = sortValue,
    ) => {
        const nextFilter = buildFilter(
            nextPriceFrom,
            nextPriceTo,
            nextSort,
            filter.tamanoPagina || 20,
        );

        if (mode === "all-products") {
            await loadAllProductsData(nextFilter);
        } else if (mode === "category" && sku) {
            await loadCategoryData(sku, nextFilter);
        } else if (mode === "search" && searchQuery) {
            await loadSearchResults(searchQuery, nextFilter);
        }
    };

    useEffect(() => {
        const initialSort = DEFAULT_SORT;
        setSortValue(initialSort);
        setPriceFrom("");
        setPriceTo("");

        const initialFilter = buildFilter("", "", initialSort, filter.tamanoPagina || 20);

        if (mode === "all-products") {
            loadAllProductsData(initialFilter);
        } else if (mode === "category" && sku) {
            loadCategoryData(sku, initialFilter);
        } else if (mode === "search" && searchQuery) {
            loadSearchResults(searchQuery, initialFilter);
        }
    }, [mode, sku, searchQuery]);

    useEffect(() => {
        if (mode !== "category" || !sku || loading || error || !category || category.sku !== sku) {
            return;
        }

        trackMetricViewOnce("CATEGORY_VIEW", { entitySku: sku }, location.key);
    }, [category, error, loading, location.key, mode, sku]);

    useEffect(() => {
        if (inView && !loading && !loadingMore && pagination.actualPage < pagination.cantPages) {
            loadMoreProducts();
        }
    }, [
        inView,
        loading,
        loadingMore,
        pagination.actualPage,
        pagination.cantPages,
        loadMoreProducts,
    ]);

    const handleProductClick = (productSku: string) => {
        const cacheKey =
            mode === "category"
                ? `category:${sku}`
                : mode === "search"
                  ? `search:${searchQuery}`
                  : "products";
        saveListingCache(cacheKey);
        navigate(`/productos/${productSku}`, {
            state: mode === "category" ? { fromCategorySku: sku } : undefined,
        });
    };

    const handleCategoryClick = (nextCategory: Category) => {
        trackMetric("CATEGORY_CLICK", { entitySku: nextCategory.sku });
        navigate(`/categoria/${nextCategory.sku}`);
    };

    const handleSortChange = (nextSort: string) => {
        setSortValue(nextSort);
        applyFilter(priceFrom, priceTo, nextSort);
    };

    const handleClearAll = () => {
        setPriceFrom("");
        setPriceTo("");
        setSortValue(DEFAULT_SORT);
        if (mode === "category") {
            navigate("/productos");
            return;
        }
        applyFilter("", "", DEFAULT_SORT);
    };

    const title =
        mode === "all-products"
            ? "Productos"
            : mode === "search"
              ? searchQuery
                  ? `Busqueda: ${searchQuery}`
                  : "Busqueda"
              : category?.nombre || (sku ? sku.replace(/-/g, " ").toUpperCase() : "");

    const noProductsMessage =
        mode === "all-products"
            ? "No se encontraron productos"
            : mode === "search"
              ? `No se encontraron resultados para "${searchQuery}"`
              : "No se encontraron productos en esta categoria";

    const activeChips = useMemo(() => {
        const chips: { key: string; label: string; onClear?: () => void }[] = [];

        if (mode === "category" && category) {
            chips.push({
                key: "category",
                label: `Categoria: ${category.nombre}`,
                onClear: () => navigate("/productos"),
            });
        }

        if (mode === "search" && searchQuery) {
            chips.push({ key: "search", label: `Busqueda: ${searchQuery}` });
        }

        if (filter.precioDesde !== undefined) {
            chips.push({
                key: "from",
                label: `Desde $${filter.precioDesde.toLocaleString("es-AR")}`,
                onClear: () => {
                    setPriceFrom("");
                    applyFilter("", priceTo, sortValue);
                },
            });
        }

        if (filter.precioHasta !== undefined) {
            chips.push({
                key: "to",
                label: `Hasta $${filter.precioHasta.toLocaleString("es-AR")}`,
                onClear: () => {
                    setPriceTo("");
                    applyFilter(priceFrom, "", sortValue);
                },
            });
        }

        if (filter.orden) {
            chips.push({
                key: "sort",
                label: `Orden: ${getSortLabel(filter.orden)}`,
                onClear:
                    filter.orden !== DEFAULT_SORT
                        ? () => {
                              setSortValue(DEFAULT_SORT);
                              applyFilter(priceFrom, priceTo, DEFAULT_SORT);
                          }
                        : undefined,
            });
        }

        return chips;
    }, [
        mode,
        category,
        searchQuery,
        filter.precioDesde,
        filter.precioHasta,
        filter.orden,
        navigate,
        priceFrom,
        priceTo,
        sortValue,
    ]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress size={24} sx={{ color: "#00629c" }} />
                <Typography sx={{ ml: 2, color: "var(--ink-500)", fontSize: "0.9rem" }}>
                    Cargando productos...
                </Typography>
            </Box>
        );
    }

    if (error && products.length === 0) {
        return (
            <Box sx={{ py: 8, textAlign: "center", px: 2 }}>
                <Typography sx={{ color: "var(--ink-900)", fontWeight: 600, mb: 1 }}>
                    {error}
                </Typography>
                <Typography sx={{ color: "var(--ink-500)", fontSize: "0.9rem", mb: 3 }}>
                    No pudimos cargar los productos.
                </Typography>
                <Button variant="contained" onClick={() => applyFilter()}>
                    Reintentar
                </Button>
            </Box>
        );
    }

    if (mode === "search" && !searchQuery) {
        return (
            <Box sx={{ py: 8, textAlign: "center" }}>
                <Typography sx={{ fontWeight: 600, color: "var(--ink-900)", mb: 1 }}>
                    Busqueda vacia
                </Typography>
                <Typography sx={{ color: "var(--ink-500)", fontSize: "0.9rem" }}>
                    Por favor, ingresa un termino de busqueda.
                </Typography>
            </Box>
        );
    }

    const hasActiveFilters =
        mode === "category" ||
        !!priceFrom ||
        !!priceTo ||
        sortValue !== DEFAULT_SORT ||
        filter.precioDesde !== undefined ||
        filter.precioHasta !== undefined ||
        filter.orden !== DEFAULT_SORT;

    return (
        <main
            style={{
                padding: isMobile ? "16px 12px 60px" : "32px 32px 60px",
                position: "relative",
                zIndex: 2,
            }}
        >
            <ListingHeader
                title={title}
                count={pagination.cantTotal || products.length}
                mode={mode}
                breadcrumbs={breadcrumbs}
                sortValue={sortValue}
                viewMode={viewMode}
                onSortChange={handleSortChange}
                onViewModeChange={setViewMode}
            />

            <ActiveFilters chips={activeChips} />

            {/* Filtros en barra horizontal (arriba) */}
            {!isMobile && posicionFiltros === "arriba" && (
                <FilterBarra
                    categories={sortedCategories}
                    getSubcategories={getSubcategories}
                    categoriesLoading={categoriesLoading}
                    activeSku={mode === "category" ? sku : undefined}
                    priceFrom={priceFrom}
                    priceTo={priceTo}
                    onPriceFromChange={setPriceFrom}
                    onPriceToChange={setPriceTo}
                    onApplyPrice={() => applyFilter()}
                    onClearAll={handleClearAll}
                    onCategoryClick={handleCategoryClick}
                    hasActiveFilters={hasActiveFilters}
                />
            )}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        !isMobile && posicionFiltros === "izquierda" ? "300px 1fr" : "1fr",
                    gap: 28,
                }}
            >
                {/* Filtros en rail lateral (izquierda) */}
                {!isMobile && posicionFiltros === "izquierda" && (
                    <FilterRail
                        categories={sortedCategories}
                        getSubcategories={getSubcategories}
                        categoriesLoading={categoriesLoading}
                        activeSku={mode === "category" ? sku : undefined}
                        priceFrom={priceFrom}
                        priceTo={priceTo}
                        onPriceFromChange={setPriceFrom}
                        onPriceToChange={setPriceTo}
                        onApplyPrice={() => applyFilter()}
                        onClearAll={handleClearAll}
                        onCategoryClick={handleCategoryClick}
                        hasActiveFilters={hasActiveFilters}
                    />
                )}

                <div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                viewMode === "grid"
                                    ? isMobile
                                        ? "repeat(2, minmax(0, 1fr))"
                                        : `repeat(${posicionFiltros === "izquierda" ? 3 : config.layout.intColumnasDesktop}, minmax(0, 1fr))`
                                    : "1fr",
                            gap: viewMode === "grid" ? 18 : 14,
                        }}
                    >
                        {products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                variant="showcase-small"
                                layout={viewMode === "grid" ? "card" : "row"}
                                onClick={() => handleProductClick(product.sku)}
                            />
                        ))}
                    </div>

                    {(loadingMore || pagination.actualPage < pagination.cantPages) && (
                        <div
                            ref={ref}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 12,
                                marginTop: 48,
                                padding: 24,
                            }}
                        >
                            <CircularProgress size={20} sx={{ color: "#00629c" }} />
                            <span style={{ color: "var(--ink-500)", fontSize: 13 }}>
                                Cargando mas productos...
                            </span>
                        </div>
                    )}

                    {error && products.length > 0 && (
                        <div style={{ textAlign: "center", marginTop: 32 }}>
                            <button className="pill" onClick={loadMoreProducts}>
                                Reintentar
                            </button>
                        </div>
                    )}

                    {!loading && products.length === 0 && !error && (
                        <div
                            style={{ textAlign: "center", marginTop: 64, color: "var(--ink-500)" }}
                        >
                            {noProductsMessage}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default ProductListingPage;
